import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isProduction } from '@/lib/growthIntel/env';
import {
  trackGiEvent, initTestFlag, markInternalVisitor, isInternalVisitor,
  type GiClientEvent,
} from '@/lib/growthIntel/tracker';

/**
 * Wires the first-party interaction tracker:
 *  - one delegated document click listener for tel:/mailto:/data-gi-cta/outbound
 *  - ONE dataLayer spa_page_view push per SPA navigation (never on initial load,
 *    because GTM's own page view already covers that)
 *  - an internal-traffic flag for GA4 filtering
 *
 * Everything no-ops unless the site is running on the production host.
 */
export function useGrowthIntelTracking(): void {
  const location = useLocation();
  const firstLoad = useRef(true);

  // Mark internal visitors + sticky test flag.
  useEffect(() => {
    initTestFlag();
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      markInternalVisitor();
    }
  }, [location.pathname]);

  // Delegated click listener.
  useEffect(() => {
    if (!isProduction()) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest('a[href], [data-gi-cta]') as HTMLElement | null;
      if (!el) return;

      const label = (el.getAttribute('data-gi-cta') || '').trim();
      const href = el.getAttribute('href') || '';

      // tel: — record only the static label, never the number.
      if (href.toLowerCase().startsWith('tel:')) {
        trackGiEvent('tel_click', { label: label || 'phone', category: 'contact' });
        return;
      }
      // mailto: — record only the label, never the address.
      if (href.toLowerCase().startsWith('mailto:')) {
        trackGiEvent('mailto_click', { label: label || 'email', category: 'contact' });
        return;
      }
      // Explicit CTA.
      if (label) {
        let targetPath = href;
        try { if (href && href.startsWith('http')) targetPath = new URL(href, window.location.origin).pathname; } catch { /* keep */ }
        trackGiEvent('cta_click', { label, target_path: targetPath || window.location.pathname });
        return;
      }
      // Outbound link — host only.
      if (href && /^https?:\/\//i.test(href)) {
        try {
          const host = new URL(href).hostname.toLowerCase();
          if (host && host !== window.location.hostname) trackGiEvent('outbound_click', { host });
        } catch { /* ignore */ }
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  // ONE spa_page_view dataLayer push per navigation (not on first load).
  useEffect(() => {
    if (!isProduction()) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    try {
      const w = window as unknown as { dataLayer?: unknown[] };
      if (Array.isArray(w.dataLayer)) {
        w.dataLayer.push({
          event: 'spa_page_view',
          page_path: location.pathname + location.search,
          page_title: document.title,
        });
      }
    } catch { /* ignore */ }
  }, [location.pathname, location.search]);

  // Push an internal-traffic hint so a GA4 filter can exclude staff.
  useEffect(() => {
    if (!isProduction() || !isInternalVisitor()) return;
    try {
      const w = window as unknown as { dataLayer?: unknown[] };
      if (Array.isArray(w.dataLayer)) w.dataLayer.push({ traffic_type: 'internal' });
    } catch { /* ignore */ }
  }, []);
}

/** Convenience re-export so pages can fire a client start/error event. */
export function trackClientEvent(event: GiClientEvent, props?: Record<string, unknown>): void {
  trackGiEvent(event, props);
}